import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card,
  CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, LinearProgress, IconButton, Alert,
  FormControlLabel, Switch, Collapse, Divider, FormControl,
  InputLabel, Select,
} from '@mui/material';
import {
  AccountTree, Add, Edit, Delete, Close, Warning, CheckCircle,
  KeyboardArrowDown, KeyboardArrowUp, Star, Person, TrendingUp,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

// ── API ───────────────────────────────────────────────────────────────────────

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      ...(opts.headers || {}),
    },
  }).then(async (r) => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.message || 'Error');
    return d;
  });
}

// ── Constants ─────────────────────────────────────────────────────────────────

const READINESS: Record<string, { label: string; color: string; chipColor: any }> = {
  ready_now:     { label: 'Ready Now',     color: '#2e7d32', chipColor: 'success' },
  ready_1_year:  { label: 'Ready ~1 Year', color: '#1565c0', chipColor: 'primary' },
  ready_2_years: { label: 'Ready ~2 Years',color: '#7b1fa2', chipColor: 'secondary' },
  long_term:     { label: 'Long-Term',     color: '#f57c00', chipColor: 'warning' },
  watch:         { label: 'Watch List',    color: '#546e7a', chipColor: 'default' },
};

const RETENTION_RISK: Record<string, { label: string; color: any }> = {
  low:    { label: 'Low',    color: 'success' },
  medium: { label: 'Medium', color: 'warning' },
  high:   { label: 'High',   color: 'error' },
};

const RISK_COLORS: Record<string, string> = {
  low: '#2e7d32', medium: '#f57c00', high: '#c62828',
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface SuccessorEntry {
  id: string; planId: string; successorId: string; readiness: string;
  rank: number; developmentNotes: string; gaps: string; retentionRisk: string;
  successor?: { firstName: string; lastName: string; department?: { name: string }; position?: { title: string } };
}

interface SuccessionPlan {
  id: string; positionTitle: string; status: string; notes: string;
  isCritical: boolean; targetDate: string; incumbentId?: string;
  incumbent?: { firstName: string; lastName: string; department?: { name: string } };
  successors?: SuccessorEntry[];
}

interface BenchRow {
  planId: string; positionTitle: string; incumbent: string; isCritical: boolean;
  totalSuccessors: number; readyNow: number; riskLevel: string;
  byReadiness: Record<string, number>;
}

interface TalentEntry {
  employeeId: string; name: string; department: string; position: string;
  plans: Array<{ planId: string; positionTitle: string; readiness: string; rank: number; retentionRisk: string }>;
}

interface Stats {
  totalPlans: number; criticalPlans: number; coveredPlans: number;
  uncoveredCritical: number; readyNow: number;
}

// ── StatCard ──────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon, color }: any) {
  return (
    <Card sx={{ textAlign: 'center', p: 1 }}>
      <CardContent>
        <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
        <Typography variant="h4" fontWeight={700} color={color}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </CardContent>
    </Card>
  );
}

// ── Plan Card ─────────────────────────────────────────────────────────────────

function PlanCard({ plan, onEdit, onDelete, onAddSuccessor, onEditSuccessor, onRemoveSuccessor }: {
  plan: SuccessionPlan;
  onEdit: () => void;
  onDelete: () => void;
  onAddSuccessor: () => void;
  onEditSuccessor: (e: SuccessorEntry) => void;
  onRemoveSuccessor: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const successors = [...(plan.successors ?? [])].sort((a, b) => a.rank - b.rank);
  const hasReadyNow = successors.some((s) => s.readiness === 'ready_now');
  const riskLevel = successors.length === 0 ? 'high' : hasReadyNow ? 'low' : 'medium';

  return (
    <Card sx={{
      mb: 2,
      border: plan.isCritical ? '2px solid #c62828' : '1px solid #e0e0e0',
      borderLeft: `5px solid ${RISK_COLORS[riskLevel]}`,
    }}>
      <CardContent sx={{ pb: '8px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" fontWeight={700}>{plan.positionTitle}</Typography>
              {plan.isCritical && (
                <Chip label="Critical" size="small" color="error" icon={<Star style={{ fontSize: 14 }} />} />
              )}
              <Chip
                label={riskLevel === 'high' ? 'No Coverage' : riskLevel === 'medium' ? 'Partial Coverage' : 'Covered'}
                size="small"
                color={riskLevel === 'high' ? 'error' : riskLevel === 'medium' ? 'warning' : 'success'}
              />
              <Chip label={plan.status} size="small" variant="outlined" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Incumbent: <b>{plan.incumbent ? `${plan.incumbent.firstName} ${plan.incumbent.lastName}` : '—'}</b>
              {plan.incumbent?.department && ` · ${plan.incumbent.department.name}`}
              {plan.targetDate && ` · Target: ${new Date(plan.targetDate).toLocaleDateString('en-HK', { year: 'numeric', month: 'short' })}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            <Tooltip title="Add Successor">
              <IconButton size="small" onClick={onAddSuccessor}><Add fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Edit Plan">
              <IconButton size="small" onClick={onEdit}><Edit fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Delete Plan">
              <IconButton size="small" color="error" onClick={onDelete}><Delete fontSize="small" /></IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setOpen((o) => !o)}>
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </Box>
        </Box>

        {/* Successor summary chips */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          {successors.length === 0 ? (
            <Typography variant="caption" color="error">⚠ No successors identified</Typography>
          ) : (
            successors.map((s, i) => (
              <Chip
                key={s.id}
                label={`${i + 1}. ${s.successor ? `${s.successor.firstName} ${s.successor.lastName}` : '?'} · ${READINESS[s.readiness]?.label ?? s.readiness}`}
                size="small"
                color={READINESS[s.readiness]?.chipColor ?? 'default'}
                variant="outlined"
                onClick={() => setOpen(true)}
              />
            ))
          )}
        </Box>
      </CardContent>

      {/* Expanded successor detail */}
      <Collapse in={open}>
        <Divider />
        <Box sx={{ px: 2, py: 1 }}>
          {successors.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No successors yet. Click + to add one.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#fafafa' }}>
                  <TableCell><b>Rank</b></TableCell>
                  <TableCell><b>Successor</b></TableCell>
                  <TableCell><b>Readiness</b></TableCell>
                  <TableCell><b>Retention Risk</b></TableCell>
                  <TableCell><b>Gaps</b></TableCell>
                  <TableCell><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {successors.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      <Typography fontWeight={700} color={s.rank === 1 ? 'primary' : 'text.primary'}>
                        #{s.rank}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {s.successor ? `${s.successor.firstName} ${s.successor.lastName}` : s.successorId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(s.successor as any)?.department?.name ?? ''}{(s.successor as any)?.position?.title ? ` · ${(s.successor as any).position.title}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={READINESS[s.readiness]?.label ?? s.readiness}
                        size="small"
                        color={READINESS[s.readiness]?.chipColor ?? 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={RETENTION_RISK[s.retentionRisk]?.label ?? s.retentionRisk}
                        size="small"
                        color={RETENTION_RISK[s.retentionRisk]?.color ?? 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="caption" color="text.secondary">{s.gaps || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEditSuccessor(s)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton size="small" color="error" onClick={() => onRemoveSuccessor(s.id)}><Delete fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {plan.notes && (
            <Box sx={{ mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary"><b>Notes:</b> {plan.notes}</Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Card>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function SuccessionPage() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');

  const [tab, setTab] = useState(0);
  const [plans, setPlans] = useState<SuccessionPlan[]>([]);
  const [bench, setBench] = useState<BenchRow[]>([]);
  const [talent, setTalent] = useState<TalentEntry[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Dialogs
  const [planDialog, setPlanDialog] = useState<Partial<SuccessionPlan> | null>(null);
  const [successorDialog, setSuccessorDialog] = useState<{
    planId: string; entryId?: string;
    successorId: string; readiness: string; rank: number;
    developmentNotes: string; gaps: string; retentionRisk: string;
  } | null>(null);
  const [deletePlanId, setDeletePlanId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [p, b, t, s] = await Promise.all([
        apiFetch('/api/succession/plans'),
        apiFetch('/api/succession/bench-strength'),
        apiFetch('/api/succession/talent-pool'),
        apiFetch('/api/succession/stats'),
      ]);
      setPlans(p);
      setBench(b);
      setTalent(t);
      setStats(s);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (employees.length === 0) {
      apiFetch('/api/employees?limit=500').then((r) => setEmployees(r.data || r || [])).catch(() => {});
    }
  }, [employees.length]);

  // ── Plan CRUD ────────────────────────────────────────────────────────────────

  const savePlan = async () => {
    if (!planDialog) return;
    try {
      if (planDialog.id) {
        await apiFetch(`/api/succession/plans/${planDialog.id}`, { method: 'PATCH', body: JSON.stringify(planDialog) });
      } else {
        await apiFetch('/api/succession/plans', { method: 'POST', body: JSON.stringify(planDialog) });
      }
      setPlanDialog(null);
      load();
    } catch (e: any) { setError(e.message); }
  };

  const deletePlan = async () => {
    if (!deletePlanId) return;
    try {
      await apiFetch(`/api/succession/plans/${deletePlanId}`, { method: 'DELETE' });
      setDeletePlanId(null);
      load();
    } catch (e: any) { setError(e.message); }
  };

  // ── Successor CRUD ───────────────────────────────────────────────────────────

  const saveSuccessor = async () => {
    if (!successorDialog) return;
    try {
      if (successorDialog.entryId) {
        await apiFetch(`/api/succession/successors/${successorDialog.entryId}`, {
          method: 'PATCH', body: JSON.stringify(successorDialog),
        });
      } else {
        await apiFetch('/api/succession/successors', {
          method: 'POST', body: JSON.stringify(successorDialog),
        });
      }
      setSuccessorDialog(null);
      load();
    } catch (e: any) { setError(e.message); }
  };

  const removeSuccessor = async (id: string) => {
    try {
      await apiFetch(`/api/succession/successors/${id}`, { method: 'DELETE' });
      load();
    } catch (e: any) { setError(e.message); }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  const criticalUncovered = plans.filter((p) => p.isCritical && (!p.successors || p.successors.length === 0));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountTree sx={{ fontSize: 32, color: '#6a1b9a' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Succession Planning</Typography>
            <Typography variant="body2" color="text.secondary">Identify successors, track readiness, and protect key roles</Typography>
          </Box>
        </Box>
        {isHR && (
          <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: '#6a1b9a' }}
            onClick={() => setPlanDialog({ positionTitle: '', status: 'active', isCritical: false })}>
            New Plan
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Critical uncovered alert */}
      {criticalUncovered.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>
          <b>{criticalUncovered.length} critical role{criticalUncovered.length > 1 ? 's' : ''} with no identified successor:</b>{' '}
          {criticalUncovered.map((p) => p.positionTitle).join(', ')}
        </Alert>
      )}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 'auto' }} sx={{ flex: 1 }}>
            <StatCard label="Total Plans" value={stats.totalPlans} icon={<AccountTree />} color="#6a1b9a" />
          </Grid>
          <Grid size={{ xs: 6, sm: 'auto' }} sx={{ flex: 1 }}>
            <StatCard label="Critical Roles" value={stats.criticalPlans} icon={<Star />} color="#c62828" />
          </Grid>
          <Grid size={{ xs: 6, sm: 'auto' }} sx={{ flex: 1 }}>
            <StatCard label="Covered" value={stats.coveredPlans} icon={<CheckCircle />} color="#2e7d32" />
          </Grid>
          <Grid size={{ xs: 6, sm: 'auto' }} sx={{ flex: 1 }}>
            <StatCard label="Critical Gaps" value={stats.uncoveredCritical} icon={<Warning />} color="#c62828" />
          </Grid>
          <Grid size={{ xs: 6, sm: 'auto' }} sx={{ flex: 1 }}>
            <StatCard label="Ready Now" value={stats.readyNow} icon={<TrendingUp />} color="#2e7d32" />
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Succession Plans" />
          <Tab label="Bench Strength" />
          <Tab label="Talent Pool" />
        </Tabs>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ── PLANS ── */}
      {tab === 0 && (
        <Box>
          {plans.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <AccountTree sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
              <Typography>No succession plans yet. Click "New Plan" to start planning.</Typography>
            </Paper>
          ) : (
            plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                onEdit={() => setPlanDialog({ ...plan })}
                onDelete={() => setDeletePlanId(plan.id)}
                onAddSuccessor={() => setSuccessorDialog({
                  planId: plan.id, successorId: '', readiness: 'watch',
                  rank: (plan.successors?.length ?? 0) + 1,
                  developmentNotes: '', gaps: '', retentionRisk: 'low',
                })}
                onEditSuccessor={(e) => setSuccessorDialog({
                  planId: plan.id, entryId: e.id,
                  successorId: e.successorId, readiness: e.readiness,
                  rank: e.rank, developmentNotes: e.developmentNotes ?? '',
                  gaps: e.gaps ?? '', retentionRisk: e.retentionRisk,
                })}
                onRemoveSuccessor={removeSuccessor}
              />
            ))
          )}
        </Box>
      )}

      {/* ── BENCH STRENGTH ── */}
      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><b>Position</b></TableCell>
                <TableCell><b>Incumbent</b></TableCell>
                <TableCell><b>Critical?</b></TableCell>
                <TableCell><b>Total Successors</b></TableCell>
                <TableCell><b>Ready Now</b></TableCell>
                <TableCell><b>1 Year</b></TableCell>
                <TableCell><b>2+ Years</b></TableCell>
                <TableCell><b>Risk</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bench.map((row) => (
                <TableRow key={row.planId} hover>
                  <TableCell><Typography fontWeight={600}>{row.positionTitle}</Typography></TableCell>
                  <TableCell>{row.incumbent ?? '—'}</TableCell>
                  <TableCell>{row.isCritical && <Chip label="Critical" size="small" color="error" />}</TableCell>
                  <TableCell align="center">{row.totalSuccessors}</TableCell>
                  <TableCell align="center">
                    {row.byReadiness.ready_now > 0
                      ? <Chip label={row.byReadiness.ready_now} size="small" color="success" />
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell align="center">
                    {(row.byReadiness.ready_1_year ?? 0) > 0
                      ? <Chip label={row.byReadiness.ready_1_year} size="small" color="primary" />
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell align="center">
                    {((row.byReadiness.ready_2_years ?? 0) + (row.byReadiness.long_term ?? 0)) > 0
                      ? <Chip label={(row.byReadiness.ready_2_years ?? 0) + (row.byReadiness.long_term ?? 0)} size="small" color="secondary" />
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.riskLevel === 'high' ? '⚠ High' : row.riskLevel === 'medium' ? 'Medium' : 'Low'}
                      size="small"
                      color={row.riskLevel === 'high' ? 'error' : row.riskLevel === 'medium' ? 'warning' : 'success'}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {bench.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    No data yet. Create plans and add successors to see bench strength.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── TALENT POOL ── */}
      {tab === 2 && (
        <Box>
          {talent.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Person sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
              <Typography>No talent pool data yet.</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {talent.map((emp) => {
                const bestReadiness = emp.plans.reduce((best, p) => {
                  const order: Record<string, number> = { ready_now: 0, ready_1_year: 1, ready_2_years: 2, long_term: 3, watch: 4 };
                  return (order[p.readiness] ?? 99) < (order[best] ?? 99) ? p.readiness : best;
                }, 'watch');
                const highRisk = emp.plans.some((p) => p.retentionRisk === 'high');
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={emp.employeeId}>
                    <Card sx={{ border: highRisk ? '2px solid #c62828' : '1px solid #e0e0e0' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="h6" fontWeight={700}>{emp.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {emp.department}{emp.position ? ` · ${emp.position}` : ''}
                            </Typography>
                          </Box>
                          <Chip
                            label={READINESS[bestReadiness]?.label ?? bestReadiness}
                            size="small"
                            color={READINESS[bestReadiness]?.chipColor ?? 'default'}
                          />
                        </Box>
                        {highRisk && (
                          <Chip label="⚠ High Retention Risk" size="small" color="error" sx={{ mt: 1 }} />
                        )}
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Successor for:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {emp.plans.map((p, i) => (
                              <Chip
                                key={i}
                                label={`#${p.rank} ${p.positionTitle}`}
                                size="small"
                                variant="outlined"
                                color={READINESS[p.readiness]?.chipColor ?? 'default'}
                              />
                            ))}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {/* ── PLAN DIALOG ── */}
      <Dialog open={!!planDialog} onClose={() => setPlanDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {planDialog?.id ? 'Edit Plan' : 'New Succession Plan'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setPlanDialog(null)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Position Title" fullWidth required
              value={planDialog?.positionTitle ?? ''}
              onChange={(e) => setPlanDialog((p) => ({ ...p, positionTitle: e.target.value }))}
              placeholder="e.g. Head of Finance, VP Engineering" />
            <FormControl fullWidth>
              <InputLabel>Current Incumbent</InputLabel>
              <Select
                value={planDialog?.incumbentId ?? ''}
                label="Current Incumbent"
                onChange={(e) => setPlanDialog((p) => ({ ...p, incumbentId: e.target.value }))}>
                <MenuItem value=""><em>None / Vacant</em></MenuItem>
                {employees.map((emp: any) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName}{emp.department?.name ? ` — ${emp.department.name}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField select label="Status" fullWidth
              value={planDialog?.status ?? 'active'}
              onChange={(e) => setPlanDialog((p) => ({ ...p, status: e.target.value }))}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="review">Under Review</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </TextField>
            <TextField label="Target Readiness Date" type="date" fullWidth
              value={planDialog?.targetDate ?? ''}
              onChange={(e) => setPlanDialog((p) => ({ ...p, targetDate: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
            <FormControlLabel
              control={
                <Switch
                  checked={planDialog?.isCritical ?? false}
                  onChange={(e) => setPlanDialog((p) => ({ ...p, isCritical: e.target.checked }))}
                  color="error"
                />
              }
              label="Critical Role (flag for priority attention)"
            />
            <TextField label="Notes" fullWidth multiline rows={2}
              value={planDialog?.notes ?? ''}
              onChange={(e) => setPlanDialog((p) => ({ ...p, notes: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={savePlan} disabled={!planDialog?.positionTitle}
            sx={{ bgcolor: '#6a1b9a' }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── SUCCESSOR DIALOG ── */}
      <Dialog open={!!successorDialog} onClose={() => setSuccessorDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {successorDialog?.entryId ? 'Edit Successor' : 'Add Successor'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setSuccessorDialog(null)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Successor</InputLabel>
              <Select
                value={successorDialog?.successorId ?? ''}
                label="Successor"
                onChange={(e) => setSuccessorDialog((p) => p ? { ...p, successorId: e.target.value } : null)}>
                {employees.map((emp: any) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName}{emp.department?.name ? ` — ${emp.department.name}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField select label="Readiness" fullWidth
              value={successorDialog?.readiness ?? 'watch'}
              onChange={(e) => setSuccessorDialog((p) => p ? { ...p, readiness: e.target.value } : null)}>
              {Object.entries(READINESS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.label}</MenuItem>
              ))}
            </TextField>
            <TextField label="Rank (1 = Primary)" type="number" fullWidth
              value={successorDialog?.rank ?? 1}
              onChange={(e) => setSuccessorDialog((p) => p ? { ...p, rank: Number(e.target.value) } : null)}
              inputProps={{ min: 1, max: 10 }} />
            <TextField select label="Retention Risk" fullWidth
              value={successorDialog?.retentionRisk ?? 'low'}
              onChange={(e) => setSuccessorDialog((p) => p ? { ...p, retentionRisk: e.target.value } : null)}>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </TextField>
            <TextField label="Development Notes" fullWidth multiline rows={2}
              value={successorDialog?.developmentNotes ?? ''}
              onChange={(e) => setSuccessorDialog((p) => p ? { ...p, developmentNotes: e.target.value } : null)}
              placeholder="Ongoing development activities, coaching, stretch assignments..." />
            <TextField label="Key Gaps" fullWidth multiline rows={2}
              value={successorDialog?.gaps ?? ''}
              onChange={(e) => setSuccessorDialog((p) => p ? { ...p, gaps: e.target.value } : null)}
              placeholder="Skills or experience gaps to address before readiness..." />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessorDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveSuccessor}
            disabled={!successorDialog?.successorId}
            sx={{ bgcolor: '#6a1b9a' }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DELETE CONFIRM ── */}
      <Dialog open={!!deletePlanId} onClose={() => setDeletePlanId(null)} maxWidth="xs">
        <DialogTitle>Delete Succession Plan?</DialogTitle>
        <DialogContent>
          <Typography>This will permanently remove the plan and all successor entries. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePlanId(null)}>Cancel</Button>
          <Button onClick={deletePlan} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
